import React, { useEffect, useRef, useState, type JSX } from "react";
import { useLocation, useNavigate } from "react-router";
import { getMenus, type MenuItem } from "@/components/navigation/routes-menus";
import { api } from "@/services/apiService";
import { useBackStore } from "@/stores/backStore";

function findMenuByHref(menus: MenuItem[], href: string): MenuItem | null {
  for (const menu of menus) {
    if (menu.href === href) return menu;
    if (menu.children) {
      const found = findMenuByHref(menu.children, href);
      if (found) return found;
    }
  }
  return null;
}

export function FooterNavLite(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [allMenus, setAllMenus] = useState<MenuItem[]>([]);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuPopupRef = useRef<HTMLDivElement>(null);
  const [hasDoc, setHasDoc] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  const [docLoading, setDocLoading] = useState(false);
  const [docError, setDocError] = useState("");
  const [doc, setDoc] = useState<{
    titulo: string;
    contenido_html: string;
  } | null>(null);

  const backRoute = useBackStore((s) => s.backRoute);
  const clearBack = useBackStore((s) => s.clearBack);

  // Footer measurement to reserve space at bottom
  const footerRef = useRef<HTMLDivElement>(null);
  const prevPaddingRef = useRef<string | null>(null);

  useEffect(() => {
    setAllMenus(getMenus());
  }, []);

  useEffect(() => {
    const m = findMenuByHref(allMenus, location.pathname);
    setHasDoc(!!m?.id_documentacion);
  }, [allMenus, location.pathname]);

  // Reserve space for footer
  useEffect(() => {
    const el = footerRef.current;
    if (!el) return;
    const applyPadding = () => {
      const h = el.offsetHeight || 0;
      if (prevPaddingRef.current === null) {
        prevPaddingRef.current = window.getComputedStyle(
          document.body
        ).paddingBottom;
      }
      document.body.style.paddingBottom = `${h}px`;
      document.documentElement.style.setProperty(
        "--footer-offset",
        `${h + 16}px`
      );
    };
    applyPadding();
    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(applyPadding);
      ro.observe(el);
    }
    window.addEventListener("resize", applyPadding);
    return () => {
      if (ro) ro.disconnect();
      window.removeEventListener("resize", applyPadding);
      if (prevPaddingRef.current !== null) {
        document.body.style.paddingBottom = prevPaddingRef.current;
      } else {
        document.body.style.removeProperty("padding-bottom");
      }
    };
  }, []);

  const handleBack = () => {
    if (backRoute) {
      navigate(backRoute);
      clearBack();
    } else {
      navigate(-1);
    }
  };
  const handleHome = () => navigate("/home");
  const toggleMenu = () => setIsMenuOpen((v) => !v);

  const handleMenuNavigation = (href: string | null) => {
    if (!href) return;
    if (href.startsWith("http://") || href.startsWith("https://")) {
      window.location.href = href;
      return;
    }
    navigate(href);
    setIsMenuOpen(false);
  };

  const cargarDoc = async (id: number) => {
    setDocLoading(true);
    setDocError("");
    setDoc(null);
    try {
      const res = await api.get(`/api/config/get-documentacion?id=${id}`);
      if (res.data && Array.isArray(res.data) && res.data[0]) {
        const item = res.data[0] as {
          titulo?: string;
          contenido_html?: string;
        };
        const rawHtml = item.contenido_html || "<p>Sin contenido.</p>";
        // Decodificar entidades HTML (&lt; &gt; &amp; etc.) si vienen escapadas
        const decodedHtml = /&lt;\/?[a-zA-Z]/.test(rawHtml)
          ? (() => {
              const textarea = document.createElement("textarea");
              textarea.innerHTML = rawHtml;
              return textarea.value;
            })()
          : rawHtml;
        setDoc({
          titulo: item.titulo || "Documentación",
          contenido_html: decodedHtml,
        });
      } else {
        setDoc({
          titulo: "Documentación",
          contenido_html: "<p>Sin contenido.</p>",
        });
      }
    } catch (err: unknown) {
      setDocError(
        err instanceof Error ? err.message : "Error al cargar documentación"
      );
    } finally {
      setDocLoading(false);
    }
  };

  const openDocs = async () => {
    const current = findMenuByHref(allMenus, location.pathname);
    const id = current?.id_documentacion ?? null;
    if (!id) return;
    setShowDocs(true);
    void cargarDoc(id);
  };
  const closeDocs = () => setShowDocs(false);

  useEffect(() => {
    const clickOutside = (e: MouseEvent) => {
      if (
        isMenuOpen &&
        menuPopupRef.current &&
        !menuPopupRef.current.contains(e.target as Node) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(e.target as Node)
      ) {
        setIsMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", clickOutside);
    return () => document.removeEventListener("mousedown", clickOutside);
  }, [isMenuOpen]);

  const renderPopupMenuItem = (item: MenuItem): JSX.Element => (
    <React.Fragment key={item.id}>
      <button
        onClick={() => handleMenuNavigation(item.href)}
        className={`w-full text-left px-3 py-2 hover:bg-slate-100 text-sm text-slate-600 ${
          !item.href && !item.children?.length
            ? "opacity-50 cursor-not-allowed"
            : ""
        }`}
        disabled={!item.href && !item.children?.length}
      >
        {item.label}
      </button>
      {item.children && item.children.length > 0 && (
        <div className="ml-3 border-l border-slate-300 pl-2">
          {item.children.map((c) => renderPopupMenuItem(c))}
        </div>
      )}
    </React.Fragment>
  );

  return (
    <>
      {showDocs && (
        <div className="fixed inset-0 z-[70] bg-base-100 overflow-y-auto p-4">
          <div className="max-w-2xl mx-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">
                {doc?.titulo || "Documentación"}
              </h2>
              <button
                onClick={closeDocs}
                className="btn btn-xs btn-outline"
                aria-label="Cerrar documentación"
              >
                Cerrar
              </button>
            </div>
            {docLoading && (
              <div className="text-sm opacity-70">Cargando...</div>
            )}
            {docError && (
              <div className="alert alert-error p-2 text-xs mb-3">
                {docError}
              </div>
            )}
            {!docLoading && !docError && (
              <article
                className="prose max-w-none text-sm"
                dangerouslySetInnerHTML={{
                  __html: doc?.contenido_html || "<p>Sin contenido.</p>",
                }}
              />
            )}
            <div className="mt-6 flex justify-center">
              <button
                onClick={closeDocs}
                className="btn btn-primary btn-sm"
                aria-label="Cerrar"
              >
                Volver
              </button>
            </div>
          </div>
        </div>
      )}
      {isMenuOpen && (
        <div
          ref={menuPopupRef}
          className="fixed left-1/2 -translate-x-1/2 w-[94%] max-w-md max-h-[60vh] bg-slate-50 border border-slate-300 rounded shadow z-50 p-2 overflow-y-auto bottom-24"
        >
          <div className="flex justify-between items-center mb-2 px-1 text-sm font-semibold text-slate-700">
            <h3>Menú Principal</h3>
            <button onClick={toggleMenu} className="p-1" aria-label="Cerrar">
              ✕
            </button>
          </div>
          <div>{allMenus.map((item) => renderPopupMenuItem(item))}</div>
        </div>
      )}
      <div
        ref={footerRef}
        className="fixed bottom-0 left-0 right-0 z-40 flex justify-center items-center p-2"
      >
        <div className="bg-slate-50 border border-slate-300 rounded-2xl w-full max-w-lg flex justify-around items-center p-1.5">
          <button
            onClick={handleBack}
            className="p-2 rounded border border-slate-300 bg-slate-50 hover:bg-slate-100 text-slate-600"
            aria-label="Atrás"
          >
            ←
          </button>
          <button
            onClick={handleHome}
            className="p-2 rounded border border-slate-300 bg-slate-50 hover:bg-slate-100 text-slate-600"
            aria-label="Inicio"
          >
            🏠
          </button>
          <button
            ref={menuButtonRef}
            onClick={toggleMenu}
            className="p-2 rounded border border-slate-300 bg-slate-50 hover:bg-slate-100 text-slate-600"
            aria-label="Menú"
          >
            ☰
          </button>
          {hasDoc && (
            <button
              onClick={() => {
                if (!showDocs) openDocs();
              }}
              className="p-2 rounded border border-slate-300 bg-slate-50 hover:bg-slate-100 text-slate-600"
              aria-label="Ver documentación"
              disabled={showDocs}
            >
              📄
            </button>
          )}
        </div>
      </div>
    </>
  );
}
